/**
 * DOCTOR-COCKPIT-VISUAL-CONFORM-V1 -- the anatomical tooth geometry module.
 *
 * Pure per-position BUCCAL-VIEW path generation for the odontogram's
 * presentation swap: the founder-explicit ODONTOGRAM STANDARD replaces the
 * schematic occlusal glyph tiles with the professional anatomical
 * representation (crown + root(s) + gingival collar), per the banked
 * DENTAL-CHART brief (docs/design/dental-chart/DESIGN-BRIEF.md):
 *   - s.25 PER-TYPE GEOMETRY: per-FDI-position crown/root proportions and
 *     root counts (this module scales that table into the 40x88 tile box);
 *   - s.24 LAYER RECIPE: paint order gingiva -> roots -> veil -> crown ->
 *     detail (the component stacks these paths; gradient-only, zero
 *     SVG filters);
 *   - height-of-contour law: every crown bulges to full width at the
 *     cervical third then tapers to the working edge.
 *
 * HAND-AUTHORED OWNED GEOMETRY -- no external asset, no incumbent imagery,
 * no copied paths (NEVER-CLONE). Standard dental anatomy proportions only
 * (FDI / ISO 3950 positions). D-11 honesty: a clinically recognizable
 * ILLUSTRATION, never the patient's actual anatomy.
 *
 * All paths are authored in ONE canonical UPPER-tooth orientation (root
 * apex up, working edge down) inside a 40-wide x 88-tall tile; the LOWER
 * arch renders the same art vertically mirrored by the component (the
 * existing GLYPH_MIRROR idiom). React-free and side-effect-free so the
 * geometry is unit-testable by direct call (repo idiom).
 */

import type { ToothDentitionView } from './tooth-diagram-view';
import { type ToothGlyphKind, toothGlyphKind } from './tooth-diagram-view';

/** The tile box every tooth draws in (component viewBox = 0 0 40 88). */
export const TOOTH_TILE_W = 40;
export const TOOTH_TILE_H = 88;
/** The cervix line (root/crown junction) in tile coordinates. */
export const TOOTH_CERVIX_Y = 46;

export interface ToothArt {
  /** The crown silhouette (buccal view; fill = dentin body gradient). */
  readonly crown: string;
  /** Root path(s), rear-most first (a ghosted palatal root renders behind). */
  readonly roots: readonly string[];
  /** True when roots[0] is the ghosted palatal/far root (renders at
   *  reduced opacity behind the buccal roots -- brief s.24 layer 2). */
  readonly ghostFirstRoot: boolean;
  /** Developmental/buccal groove strokes over the crown (may be empty). */
  readonly grooves: string;
  /** The gingival margin arc across the tile (concave toward the bite --
   *  brief s.27: an arc per crown neck, never a wave over nothing). */
  readonly gumline: string;
}

/** Per-position buccal proportions, scaled from the brief s.25 table
 *  (drawing units -> tile units). w = crown width, ch = crown height,
 *  rh = root length. Index = FDI position digit 1..8. */
interface ToothProportions {
  readonly w: number;
  readonly ch: number;
  readonly rh: number;
}

const UPPER_PROPORTIONS: readonly ToothProportions[] = [
  { w: 27, ch: 33, rh: 37 }, // 1 central incisor
  { w: 21, ch: 29, rh: 34 }, // 2 lateral incisor
  { w: 24, ch: 33, rh: 46 }, // 3 canine (longest root)
  { w: 25, ch: 27, rh: 38 }, // 4 first premolar
  { w: 24, ch: 26, rh: 37 }, // 5 second premolar
  { w: 36, ch: 25, rh: 37 }, // 6 first molar
  { w: 33, ch: 24, rh: 35 }, // 7 second molar
  { w: 29, ch: 21, rh: 26 }, // 8 third molar
];

const LOWER_PROPORTIONS: readonly ToothProportions[] = [
  { w: 19, ch: 29, rh: 33 }, // 1
  { w: 21, ch: 29, rh: 34 }, // 2
  { w: 23, ch: 32, rh: 44 }, // 3
  { w: 24, ch: 26, rh: 37 }, // 4 (single root below)
  { w: 25, ch: 26, rh: 36 }, // 5
  { w: 37, ch: 25, rh: 36 }, // 6 (two roots)
  { w: 34, ch: 24, rh: 34 }, // 7 (two roots)
  { w: 31, ch: 22, rh: 26 }, // 8 (fused)
];

/** Scale table units into the 40-wide tile (the widest crown, 37, keeps a
 *  1.5-unit margin each side). Vertical: crown heights map near 1:1; root
 *  lengths compress slightly so the longest canine root stays in-tile. */
const SCALE_W = 37 / 37;
const SCALE_CH = 1.0;
const SCALE_RH = 0.85;

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** One tapered root cone: cervix half-width -> apex point, with a gentle
 *  distal lean. Authored upper-orientation (apex above the cervix). */
function rootCone(
  cx: number,
  cervixHalfW: number,
  apexY: number,
  lean: number,
): string {
  const y0 = TOOTH_CERVIX_Y + 2; // tucked under the crown neck
  const xL = round1(cx - cervixHalfW);
  const xR = round1(cx + cervixHalfW);
  const ax = round1(cx + lean);
  const midY = round1(apexY + (y0 - apexY) * 0.45);
  return [
    `M ${xL} ${y0}`,
    `C ${round1(xL + 0.4)} ${midY} ${round1(ax - 1.6)} ${round1(apexY + 4)} ${ax} ${round1(apexY)}`,
    `C ${round1(ax + 1.6)} ${round1(apexY + 4)} ${round1(xR - 0.4)} ${midY} ${xR} ${y0}`,
    'Z',
  ].join(' ');
}

/** The crown silhouette per family. Authored upper-orientation: neck at
 *  TOOTH_CERVIX_Y, working edge at neck + ch. Height-of-contour: full
 *  width at the cervical third, tapering toward the edge. */
function crownPath(kind: ToothGlyphKind, w: number, ch: number): string {
  const cx = TOOTH_TILE_W / 2;
  const half = w / 2;
  const neckHalf = half * 0.62;
  const contourY = TOOTH_CERVIX_Y + ch * 0.34; // the cervical-third bulge
  const edgeY = TOOTH_CERVIX_Y + ch;
  const xL = round1(cx - half);
  const xR = round1(cx + half);
  const nL = round1(cx - neckHalf);
  const nR = round1(cx + neckHalf);
  const cY = round1(contourY);
  const eY = round1(edgeY);
  const neckY = TOOTH_CERVIX_Y;

  if (kind === 'incisor') {
    // Chisel blade: near-straight incisal edge, rounded distal corner.
    const edgeHalf = half * 0.86;
    return [
      `M ${nL} ${neckY}`,
      `C ${xL} ${round1(neckY + ch * 0.16)} ${xL} ${cY} ${round1(cx - edgeHalf)} ${round1(eY - 1.6)}`,
      `Q ${round1(cx - edgeHalf * 0.6)} ${eY} ${cx} ${eY}`,
      `Q ${round1(cx + edgeHalf * 0.7)} ${round1(eY - 0.6)} ${round1(cx + edgeHalf)} ${round1(eY - 2.2)}`,
      `C ${xR} ${cY} ${xR} ${round1(neckY + ch * 0.16)} ${nR} ${neckY}`,
      'Z',
    ].join(' ');
  }
  if (kind === 'canine') {
    // Single cusp; mesial shoulder higher than the distal.
    return [
      `M ${nL} ${neckY}`,
      `C ${xL} ${round1(neckY + ch * 0.2)} ${xL} ${cY} ${round1(cx - half * 0.58)} ${round1(eY - ch * 0.28)}`,
      `L ${cx} ${eY}`,
      `L ${round1(cx + half * 0.62)} ${round1(eY - ch * 0.34)}`,
      `C ${xR} ${cY} ${xR} ${round1(neckY + ch * 0.2)} ${nR} ${neckY}`,
      'Z',
    ].join(' ');
  }
  if (kind === 'premolar') {
    // One dominant buccal cusp on a rounded table.
    return [
      `M ${nL} ${neckY}`,
      `C ${xL} ${round1(neckY + ch * 0.2)} ${xL} ${cY} ${round1(cx - half * 0.68)} ${round1(eY - ch * 0.16)}`,
      `Q ${round1(cx - half * 0.2)} ${eY} ${round1(cx + half * 0.1)} ${round1(eY - ch * 0.06)}`,
      `Q ${round1(cx + half * 0.66)} ${round1(eY - ch * 0.2)} ${round1(cx + half * 0.72)} ${round1(eY - ch * 0.24)}`,
      `C ${xR} ${cY} ${xR} ${round1(neckY + ch * 0.2)} ${nR} ${neckY}`,
      'Z',
    ].join(' ');
  }
  // Molar: a wide table with cusp bumps along the working edge.
  const bumpRise = ch * 0.12;
  return [
    `M ${nL} ${neckY}`,
    `C ${xL} ${round1(neckY + ch * 0.18)} ${xL} ${cY} ${round1(cx - half * 0.78)} ${round1(eY - bumpRise)}`,
    // two buccal bumps (the lower-first's third bump reads through the
    // groove detail rather than a third silhouette lobe at this scale)
    `Q ${round1(cx - half * 0.42)} ${round1(eY + 1)} ${round1(cx - half * 0.08)} ${round1(eY - bumpRise * 0.8)}`,
    `Q ${round1(cx + half * 0.28)} ${round1(eY + 1)} ${round1(cx + half * 0.62)} ${round1(eY - bumpRise * 0.9)}`,
    `Q ${round1(cx + half * 0.8)} ${round1(eY - bumpRise)} ${round1(cx + half * 0.78)} ${round1(eY - bumpRise)}`,
    `C ${xR} ${cY} ${xR} ${round1(neckY + ch * 0.18)} ${nR} ${neckY}`,
    'Z',
  ].join(' ');
}

/** Buccal groove strokes (premolar: one short central; molar: one or two
 *  developmental grooves running cervically from the edge). */
function groovePaths(kind: ToothGlyphKind, w: number, ch: number): string {
  const cx = TOOTH_TILE_W / 2;
  const eY = TOOTH_CERVIX_Y + ch;
  if (kind === 'premolar') {
    return `M ${cx} ${round1(eY - 2)} C ${round1(cx - 0.6)} ${round1(eY - ch * 0.3)} ${round1(cx + 0.6)} ${round1(eY - ch * 0.42)} ${cx} ${round1(eY - ch * 0.5)}`;
  }
  if (kind === 'molar') {
    const g1 = round1(cx - w * 0.18);
    const g2 = round1(cx + w * 0.16);
    return [
      `M ${g1} ${round1(eY - 1.5)} C ${round1(g1 - 0.8)} ${round1(eY - ch * 0.34)} ${round1(g1 + 0.4)} ${round1(eY - ch * 0.46)} ${round1(g1 + 0.2)} ${round1(eY - ch * 0.55)}`,
      `M ${g2} ${round1(eY - 1.5)} C ${round1(g2 + 0.8)} ${round1(eY - ch * 0.32)} ${round1(g2 - 0.4)} ${round1(eY - ch * 0.44)} ${round1(g2 - 0.2)} ${round1(eY - ch * 0.52)}`,
    ].join(' ');
  }
  return '';
}

/** Root set per position (brief s.25 root columns). Upper-orientation. */
function rootPaths(
  arch: 'upper' | 'lower',
  position: number,
  kind: ToothGlyphKind,
  w: number,
  rh: number,
): { paths: readonly string[]; ghostFirst: boolean } {
  const cx = TOOTH_TILE_W / 2;
  const apexY = round1(TOOTH_CERVIX_Y - rh);
  const half = w / 2;
  if (kind === 'incisor' || kind === 'canine') {
    return {
      paths: [rootCone(cx, half * 0.5, apexY, position === 3 ? 1.2 : 0.8)],
      ghostFirst: false,
    };
  }
  if (kind === 'premolar') {
    if (arch === 'upper' && position === 4) {
      // TWO thin roots: buccal + palatal (palatal drawn slightly inset).
      return {
        paths: [
          rootCone(cx + half * 0.28, half * 0.3, round1(apexY + 3), 1.2),
          rootCone(cx - half * 0.24, half * 0.34, apexY, -1),
        ],
        ghostFirst: false,
      };
    }
    return { paths: [rootCone(cx, half * 0.46, apexY, 0.8)], ghostFirst: false };
  }
  // Molars.
  if (position === 8) {
    // Fused, distally curved cone.
    return { paths: [rootCone(cx, half * 0.52, apexY, 2.6)], ghostFirst: false };
  }
  if (arch === 'upper') {
    // THREE: MB + DB fork with the palatal ghosted between/behind.
    return {
      paths: [
        rootCone(cx + half * 0.06, half * 0.3, round1(apexY + 2), 0.4),
        rootCone(cx - half * 0.5, half * 0.3, apexY, -2),
        rootCone(cx + half * 0.5, half * 0.3, round1(apexY + 1.5), 2),
      ],
      ghostFirst: true,
    };
  }
  // Lower molars: TWO (mesial curved + distal).
  return {
    paths: [
      rootCone(cx - half * 0.44, half * 0.32, apexY, -2.2),
      rootCone(cx + half * 0.44, half * 0.32, round1(apexY + 2), 1.6),
    ],
    ghostFirst: false,
  };
}

/** The per-tooth gingival margin arc: concave toward the bite, following
 *  the neck width, with papilla rises at the contacts (brief s.27). The
 *  fill closes upward (over the root zone) so it doubles as the tissue
 *  veil band for this tile. */
function gumlinePath(w: number): string {
  const cx = TOOTH_TILE_W / 2;
  const half = Math.min(w / 2 + 3.5, TOOTH_TILE_W / 2);
  const marginY = TOOTH_CERVIX_Y + 3.5;
  const papillaY = TOOTH_CERVIX_Y - 3.5;
  const topY = 14;
  return [
    `M 0 ${topY}`,
    `L 0 ${round1(papillaY + 1)}`,
    `Q ${round1(cx - half * 0.86)} ${round1(papillaY)} ${round1(cx - half * 0.52)} ${round1(marginY - 2.4)}`,
    `Q ${cx} ${round1(marginY + 2.2)} ${round1(cx + half * 0.52)} ${round1(marginY - 2.4)}`,
    `Q ${round1(cx + half * 0.86)} ${round1(papillaY)} ${TOOTH_TILE_W} ${round1(papillaY + 1)}`,
    `L ${TOOTH_TILE_W} ${topY}`,
    'Z',
  ].join(' ');
}

/**
 * The anatomical art for one tooth position. Deterministic and pure -- the
 * same inputs always produce the same paths (pinnable by direct call).
 * Primary (deciduous) teeth reuse the family silhouettes scaled smaller via
 * the component's CSS sizing (their glyph-kind mapping already collapses
 * primary molars onto the molar family -- toothGlyphKind, unchanged).
 */
export function anatomicalToothArt(
  toothCode: string,
  dentition: ToothDentitionView,
  arch: 'upper' | 'lower',
): ToothArt {
  const kind = toothGlyphKind(toothCode, dentition);
  const position = Math.min(
    8,
    Math.max(1, Number(toothCode.charAt(1)) || 1),
  );
  const table = arch === 'upper' ? UPPER_PROPORTIONS : LOWER_PROPORTIONS;
  const base = table[position - 1] ?? table[0];
  const scale = dentition === 'primary' ? 0.82 : 1;
  const w = base.w * SCALE_W * scale;
  const ch = base.ch * SCALE_CH * scale;
  // Root length clamps so every apex stays INSIDE the tissue band (the
  // canine keeps the longest read without piercing the band's top edge).
  const rh = Math.min(base.rh * SCALE_RH * scale, TOOTH_CERVIX_Y - 17);
  const roots = rootPaths(arch, position, kind, w, rh);
  return {
    crown: crownPath(kind, w, ch),
    roots: roots.paths,
    ghostFirstRoot: roots.ghostFirst,
    grooves: groovePaths(kind, w, ch),
    gumline: gumlinePath(w),
  };
}
